import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { func, string } from 'prop-types';
import debounce from 'lodash/debounce';
import { searchAttractions } from '../../util/api/supabaseApi';
import { IconClose, IconSearch } from '../../components';
import { publicSearchInputProps } from '../../util/inputAttributes';

import css from './HeroAutocomplete.module.css';

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 275;
const CLIENT_CACHE_TTL_MS = 5 * 60 * 1000;
const CLIENT_CACHE_MAX_ENTRIES = 100;
const attractionSearchCache = new Map();
const FALLBACK_IMAGE =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iOCIgZmlsbD0iI0Y5RkFGQiIvPgo8cGF0aCBkPSJNMjAgMTJMMjggMjBMMjAgMjhMMTIgMjBMMjAgMTJaIiBmaWxsPSIjN0IyQ0JGIi8+CjxwYXRoIGQ9Ik0yMCAxNkwyNCAyMEwyMCAyNEwxNiAyMEwyMCAxNloiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPgo=';

const normalize = value =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const trackSearchAnalytics = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
};

const readCachedSearch = key => {
  const cached = attractionSearchCache.get(key);
  if (!cached || Date.now() - cached.timestamp >= CLIENT_CACHE_TTL_MS) {
    attractionSearchCache.delete(key);
    return null;
  }

  attractionSearchCache.delete(key);
  attractionSearchCache.set(key, cached);
  return cached;
};

const writeCachedSearch = (key, payload) => {
  attractionSearchCache.delete(key);
  while (attractionSearchCache.size >= CLIENT_CACHE_MAX_ENTRIES) {
    attractionSearchCache.delete(attractionSearchCache.keys().next().value);
  }
  attractionSearchCache.set(key, { payload, timestamp: Date.now() });
};

const buildAttractionSelection = item => ({
  id: item.navigation?.attractionId || item.id,
  name: item.title,
  source: item.source || 'ticketmaster_catalog',
  ticketmasterAttractionId: item.navigation?.ticketmasterAttractionId || null,
  image: item.imageUrl || item.image_url || null,
  image_url: item.imageUrl || item.image_url || null,
  eventCount: item.eventCount || item.activeEventCount || null,
  category: item.category || null,
});

const formatCsvAttraction = attraction => {
  const eventCount = attraction.eventCount || attraction.event_count || 0;
  const category = attraction.category || 'Attraction';
  const subtitle = category;

  return {
    id: attraction.id,
    entityType: 'attraction',
    source: attraction.source || 'ticketmaster_catalog',
    title: attraction.name || attraction.attraction_name || 'Attraction',
    subtitle,
    imageUrl: attraction.image_url || attraction.imageUrl || null,
    eventCount,
    activeEventCount: eventCount,
    category,
    navigation: {
      type: 'attraction',
      attractionId: attraction.id,
      ticketmasterAttractionId: attraction.ticketmasterAttractionId || null,
    },
  };
};

const HeroAutocomplete = ({
  placeholder = 'What event do you want to see?',
  onAttractionSelect = null,
  onSearch = null,
  className = '',
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [searchState, setSearchState] = useState('idle');
  const [sections, setSections] = useState([]);
  const [error, setError] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const abortRef = useRef(null);
  const latestRequestIdRef = useRef(0);

  const hasResults = sections.some(
    section => Array.isArray(section.items) && section.items.length > 0
  );
  const flatItems = useMemo(() => {
    const items = [];
    sections.forEach(section => {
      (section.items || []).forEach(item => {
        items.push({ ...item, sectionType: section.type, key: `${section.type}-${item.id}` });
      });
    });
    return items;
  }, [sections]);
  const hasSelectableResults = flatItems.length > 0;

  const indexedSections = useMemo(() => {
    let runningIndex = -1;
    return sections.map(section => ({
      ...section,
      indexedItems: (section.items || []).map(item => {
        runningIndex += 1;
        return {
          ...item,
          optionIndex: runningIndex,
          optionKey: `${section.type}-${item.id}`,
        };
      }),
    }));
  }, [sections]);

  const resetResults = useCallback(() => {
    setSections([]);
    setError(null);
    setSearchState('idle');
    setHasSearched(false);
    setActiveIndex(-1);
  }, []);

  const performSearch = useCallback(
    async query => {
      const trimmedQuery = typeof query === 'string' ? query.trim() : '';
      const normalizedQuery = normalize(trimmedQuery);

      if (trimmedQuery.length < MIN_QUERY_LENGTH) {
        latestRequestIdRef.current += 1;
        abortRef.current?.abort();
        resetResults();
        setShowSuggestions(false);
        return;
      }

      const searchStartedAt =
        typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
      latestRequestIdRef.current += 1;
      const requestId = latestRequestIdRef.current;
      abortRef.current?.abort();

      const cached = readCachedSearch(normalizedQuery);
      if (cached) {
        const visibleSections = (cached.payload.sections || []).filter(
          section => section.type !== 'event'
        );
        setSections(visibleSections);
        setSearchState(visibleSections.length > 0 ? 'success' : 'empty');
        setHasSearched(true);
        setShowSuggestions(true);
        setActiveIndex(-1);
        trackSearchAnalytics('home_search_results_loaded', {
          query: trimmedQuery,
          result_count: (cached.payload.sections || []).reduce(
            (sum, section) => sum + ((section.items || []).length || 0),
            0
          ),
          response_time_ms: Math.round(
            (typeof performance !== 'undefined' && performance.now
              ? performance.now()
              : Date.now()) - searchStartedAt
          ),
          cached: true,
        });
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;

      setSearchState('loading');
      setError(null);
      setHasSearched(true);
      setShowSuggestions(true);

      try {
        const csvAttractions = await searchAttractions(trimmedQuery, null, {
          signal: controller.signal,
        }).catch(error => {
          if (error?.name === 'AbortError') {
            throw error;
          }
          console.warn('CSV attraction search failed:', error);
          return [];
        });

        if (requestId !== latestRequestIdRef.current) {
          return;
        }

        let attractionItems = (Array.isArray(csvAttractions) ? csvAttractions : [])
          .filter(attraction => attraction?.id && (attraction?.name || attraction?.attraction_name))
          .map(formatCsvAttraction);

        if (requestId !== latestRequestIdRef.current) {
          return;
        }

        const sections = [
          attractionItems.length
            ? {
                type: 'attraction',
                label: 'Artists & Teams',
                items: attractionItems,
              }
            : null,
        ].filter(Boolean);

        setSections(sections);
        setSearchState(sections.length > 0 ? 'success' : 'empty');
        setActiveIndex(-1);
        writeCachedSearch(normalizedQuery, { sections });
        const resultCount = sections.reduce(
          (sum, section) => sum + ((section.items || []).length || 0),
          0
        );
        trackSearchAnalytics('home_search_results_loaded', {
          query: trimmedQuery,
          result_count: resultCount,
          top_section: sections[0]?.type || 'none',
          response_time_ms: Math.round(
            (typeof performance !== 'undefined' && performance.now
              ? performance.now()
              : Date.now()) - searchStartedAt
          ),
          cached: false,
        });
        if (!resultCount) {
          trackSearchAnalytics('home_search_empty', {
            query: trimmedQuery,
          });
        }
      } catch (searchError) {
        if (searchError?.name === 'AbortError') {
          return;
        }

        if (requestId !== latestRequestIdRef.current) {
          return;
        }

        console.error('Home search failed:', searchError);
        setSections([]);
        setError('Oops, something went wrong! Please try again.');
        setSearchState('error');
        setActiveIndex(-1);
        trackSearchAnalytics('home_search_error', {
          query: trimmedQuery,
        });
      }
    },
    [resetResults]
  );

  const debouncedSearch = useMemo(
    () => debounce(value => performSearch(value), SEARCH_DEBOUNCE_MS),
    [performSearch]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      abortRef.current?.abort();
    };
  }, [debouncedSearch]);

  const handleInputChange = event => {
    const newValue = event.target.value;
    setSearchValue(newValue);
    setActiveIndex(-1);

    if (newValue.trim().length < MIN_QUERY_LENGTH) {
      debouncedSearch.cancel();
      abortRef.current?.abort();
      resetResults();
      setShowSuggestions(false);
      return;
    }

    debouncedSearch(newValue);
  };

  const handleClearSearch = event => {
    event.preventDefault();
    debouncedSearch.cancel();
    abortRef.current?.abort();
    setSearchValue('');
    resetResults();
    setShowSuggestions(false);
  };

  const handleInputFocus = () => {
    if (
      searchValue.trim().length >= MIN_QUERY_LENGTH &&
      (hasResults || searchState === 'loading' || error)
    ) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    window.setTimeout(() => {
      setShowSuggestions(false);
    }, 160);
  };

  const handleSuggestionClick = item => {
    setShowSuggestions(false);
    setActiveIndex(-1);

    trackSearchAnalytics('home_search_result_clicked', {
      query: searchValue.trim(),
      clicked_entity_type: item?.entityType || 'unknown',
      clicked_rank: Math.max(activeIndex, 0) + 1,
      clicked_title: item?.title || '',
    });

    if (
      (item?.entityType === 'attraction' || item?.entityType === 'ticketmasterAttraction') &&
      onAttractionSelect
    ) {
      onAttractionSelect(buildAttractionSelection(item));
      return;
    }

    if (onSearch) {
      onSearch(item?.title || searchValue.trim());
    }
  };

  const handleSubmit = event => {
    event.preventDefault();
    const trimmedQuery = searchValue.trim();
    if (!trimmedQuery || !hasSelectableResults) {
      return;
    }

    setShowSuggestions(false);
    setActiveIndex(-1);
    trackSearchAnalytics('home_search_view_all_clicked', {
      query: trimmedQuery,
      source: 'submit',
    });
    if (onSearch) {
      onSearch(trimmedQuery);
    }
  };

  const handleKeyDown = event => {
    if (!showSuggestions || !flatItems.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(prev => (prev + 1) % flatItems.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(prev => (prev <= 0 ? flatItems.length - 1 : prev - 1));
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setShowSuggestions(false);
      setActiveIndex(-1);
      return;
    }

    if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      const activeItem = flatItems[activeIndex];
      if (activeItem) {
        handleSuggestionClick(activeItem);
      }
    }
  };

  useEffect(() => {
    setActiveIndex(-1);
  }, [sections]);

  return (
    <div className={`${css.root} ${className}`}>
      <form onSubmit={handleSubmit}>
        <div className={css.searchContainer}>
          <div className={css.inputWrapper}>
            <button type="submit" className={css.searchButton} aria-label="Search">
              <IconSearch className={css.searchIcon} />
            </button>
            <input
              id="hero-event-search"
              name="eventSearch"
              type="text"
              value={searchValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className={css.searchInput}
              {...publicSearchInputProps}
              aria-label="Search for events, artists, teams, or venues"
              aria-expanded={showSuggestions}
              aria-autocomplete="list"
              aria-controls="hero-search-suggestions"
              aria-activedescendant={
                activeIndex >= 0 ? `hero-search-option-${activeIndex}` : undefined
              }
            />
            {searchValue ? (
              <button
                type="button"
                className={css.clearButton}
                aria-label="Clear search"
                onMouseDown={handleClearSearch}
                onClick={handleClearSearch}
              >
                <IconClose className={css.clearIcon} />
              </button>
            ) : null}
          </div>
        </div>
      </form>

      {showSuggestions && (
        <div id="hero-search-suggestions" className={css.suggestionsContainer} role="listbox">
          {searchState === 'loading' ? (
            <div className={css.loadingState}>
              <div className={css.loadingSpinner}>
                <div className={css.spinner}></div>
              </div>
              <div className={css.loadingText}>Searching…</div>
            </div>
          ) : null}

          {searchState === 'error' && error ? (
            <div className={css.errorState}>
              <div className={css.errorIcon}>⚠️</div>
              <div className={css.errorMessage}>{error}</div>
              <button
                type="button"
                className={css.retryButton}
                onMouseDown={event => {
                  event.preventDefault();
                  performSearch(searchValue);
                }}
              >
                Try Again
              </button>
            </div>
          ) : null}

          {searchState === 'empty' && hasSearched ? (
            <div className={css.emptyState}>
              <div className={css.emptyIcon}></div>
              <div className={css.emptyTitle}>No results found</div>
              <div className={css.emptyMessage}>Try a different artist, team, event, or venue.</div>
            </div>
          ) : null}

          {searchState === 'success' && hasResults ? (
            <>
              <div className={css.resultsContainer}>
                {indexedSections.map(section => (
                  <div key={section.type} className={css.resultsSection}>
                    <div className={css.sectionHeader}>
                      <span className={css.sectionTitle}>{section.label}</span>
                    </div>
                    <div className={css.suggestionsList}>
                      {section.indexedItems.map(item => {
                        const isActive = item.optionIndex === activeIndex;
                        return (
                          <div
                            key={item.optionKey}
                            id={`hero-search-option-${item.optionIndex}`}
                            role="option"
                            aria-selected={isActive}
                            className={`${css.suggestionItem} ${
                              isActive ? css.suggestionItemActive : ''
                            }`}
                            onMouseEnter={() => setActiveIndex(item.optionIndex)}
                            onMouseDown={event => {
                              event.preventDefault();
                              handleSuggestionClick(item);
                            }}
                          >
                            <div className={css.itemImageContainer}>
                              <img
                                src={item.imageUrl || FALLBACK_IMAGE}
                                alt={item.title}
                                className={css.itemImage}
                                onError={e => {
                                  e.target.src = FALLBACK_IMAGE;
                                }}
                              />
                            </div>
                            <div className={css.itemInfo}>
                              <div className={css.itemName}>{item.title}</div>
                              <div className={css.itemMeta}>{item.subtitle}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};

HeroAutocomplete.propTypes = {
  placeholder: string,
  onAttractionSelect: func,
  onSearch: func,
  className: string,
};

export default HeroAutocomplete;
